import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Tooltip, withStyles } from '@material-ui/core'
import Chip from '@material-ui/core/Chip'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Autocomplete, {
    AutocompleteChangeReason,
    AutocompleteGetTagProps,
    AutocompleteInputChangeReason,
} from '@material-ui/lab/Autocomplete'
import { SearchFacets } from 'api/graphQL/types/product.type'
import clsx from 'clsx'
import { getPTAndFamilyFromString } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import { DialogContext } from '../DialogContext'
import { filterProductTypeOptions } from './DialogFilter.utils'

const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: '#242424',
        width: '100%',
        maxWidth: 'unset',
        outline: `1px solid #0098d6`,
        boxShadow: '4px 4px 5px rgba(0,0,0,0.3)',
        fontSize: pxToRem(12),
        textAlign: 'center',
    },
    arrow: {
        '&:before': {
            border: '1px solid #0098d6',
        },
        color: theme.palette.common.white,
    },
    tooltipPlacementTop: {
        marginBottom: theme.spacing(0.6),
        marginTop: theme.spacing(0.6),
    },
}))(Tooltip)

export default HtmlTooltip

interface AutocompleteStylesProps {
    width: number
    fullWidth: boolean
}

const autoCompleteStyles = makeStyles({
    chipLabel: {
        display: 'flex',
        boxSizing: 'border-box',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        maxWidth: (props: AutocompleteStylesProps) =>
            !props.fullWidth ? 'inherit' : props.width - 127,
    },
    option: {
        color: '#5E5F61',
    },
    ellipsis: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
})

interface AutoCompleteTagsProps {
    option: string
    index: number
    selectedDisplayItems?: string[]
    getTagProps: AutocompleteGetTagProps
    width: number
    fullWidth: boolean
    isOpen?: boolean
}

const getOptionText = (option: string) => {
    return option.split('||')[0].trim()
}

const AutoCompleteTags: React.FC<AutoCompleteTagsProps> = ({
    option,
    index,
    selectedDisplayItems,
    getTagProps,
    width,
    fullWidth,
    isOpen = false,
}) => {
    const styles = autoCompleteStyles({ width, fullWidth })
    return (
        <Chip
            {...getTagProps({ index })}
            classes={{ label: styles.chipLabel }}
            variant="outlined"
            label={
                !!selectedDisplayItems && selectedDisplayItems?.length > 0 ? (
                    // Use hover tooltip to always show tooltip not only when truncated
                    !isOpen ? (
                        <HtmlTooltip
                            arrow
                            title={
                                <Typography>
                                    {selectedDisplayItems.map((item, index) => {
                                        return (
                                            <Typography
                                                key={index}
                                                variant="body2"
                                                style={{ textAlign: 'left' }}
                                            >
                                                {getOptionText(item)}
                                            </Typography>
                                        )
                                    })}
                                </Typography>
                            }
                        >
                            <div className={styles.ellipsis}>
                                {getOptionText(selectedDisplayItems[index])}
                            </div>
                        </HtmlTooltip>
                    ) : (
                        <HoverTooltip
                            text={option}
                            children={
                                <div className={styles.ellipsis}>
                                    {getOptionText(selectedDisplayItems[index])}
                                </div>
                            }
                        />
                    )
                ) : (
                    <SingleLineEllipsis text={getOptionText(option)} />
                )
            }
        />
    )
}

const useStyles = makeStyles((theme: Theme) => ({
    autocompleteRoot: {
        '& legend span': {
            fontSize: '0.8em',
            lineHeight: 1.5,
        },
        minHeight: 48,
        border: '1px solid #D9D9D9',
    },
    autocompleteRemoveBorder: {
        '>fieldset.MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
    },
    autocompleteRootNoLabel: {
        minHeight: 48,
    },
    autocompleteRootHidden: {
        width: 1,
    },
    autocompleteRootWidth: {
        width: 15,
    },
    autocompleteContainer: {
        minWidth: 150,
    },
    hide: {
        width: 0,
    },
    text: {
        fontSize: pxToRem(12),
        fontWeight: 400,
        color: '#464646',
        lineHeight: 1.5,
        paddingLeft: theme.spacing(1),
    },
    labelText: {
        fontSize: pxToRem(12),
        fontWeight: 400,
        color: '#464646',
        lineHeight: 2.3,
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
        },
    },

    ellipsis: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    option: { wordBreak: 'break-word' },
    pointer: {
        cursor: 'pointer',
    },
    autocompleteTag: {
        margin: 2,
        maxWidth: '44%',
    },
    nonFullAutoCompleteTag: {
        margin: 2,
        padding: 6,
        maxWidth: 'calc(103% - 4px)',
    },
    filterWidthNonFull: {
        minWidth: pxToRem(140),
    },
    popperStyle: {
        zIndex: 99999,
    },
    textField: {
        backgroundColor: theme.palette.common.white,
    },
}))

interface DialogFilterProps {
    name: string
    label?: string
    className?: string
    value: string | string[]
    options: string[]
    productTypeOptions?: Partial<SearchFacets>[]
    handleValue: (
        value: string | string[] | null,
        productValue?: Partial<SearchFacets>[]
    ) => void
    handleOpen?: VoidFunction
    handleClose?: VoidFunction
    handleInput?: (value: string) => void
    handleInputCheckbox?: (value: boolean) => void
    checked?: boolean
    type: 'single' | 'multiple' | 'checkbox'
    loading?: boolean
    selectedDisplayItems?: string[]
    fullWidth?: boolean
    disabled?: boolean
    isInputError?: boolean
}

export const DialogFilter: React.FC<DialogFilterProps> = ({
    name,
    label,
    className,
    value,
    options,
    productTypeOptions,
    handleValue,
    handleOpen,
    handleClose,
    handleInput,
    type,
    loading,
    selectedDisplayItems,
    fullWidth = true,
    disabled = false,
    isInputError = false,
}) => {
    const classes = useStyles()
    const {
        setIsFamilyNotSelectedInFilter,
        setIsPTNotSelectedInFilter,
    } = React.useContext(DialogContext)
    const [localInputValue, setLocalInputValue] = React.useState<string>('')
    const [localWidth, setLocalWidth] = React.useState<number>(200)
    const [hideInput, setHideInput] = React.useState<boolean>(true)

    React.useEffect(() => {
        if (!!value && value.length > 0) {
            if (name === 'families') {
                setIsFamilyNotSelectedInFilter?.(false)
            } else {
                setIsPTNotSelectedInFilter?.(false)
            }
        }
    }, [value, name])

    const handleChange = (
        _event: React.ChangeEvent<{}>,
        value: string | string[] | null,
        reason: AutocompleteChangeReason
    ) => {
        if (name === 'productTypes' && typeof value === 'object') {
            const { familyNames } = getPTAndFamilyFromString(value)
            setIsPTNotSelectedInFilter?.(false)
            handleValue(
                value,
                productTypeOptions?.filter((item) =>
                    value?.some(
                        (each) =>
                            each.trim() ===
                            `${item?.value?.trim()} || ${item?.parentProductFamily?.trim()}`
                    )
                )
            )
        } else if (name === 'families' && typeof value === 'object' && value) {
            setIsFamilyNotSelectedInFilter?.(false)
            handleValue?.(
                value,
                productTypeOptions?.filter((item) =>
                    value?.some((each) => each === item?.parentProductFamily)
                )
            )
        } else {
            handleValue(value)
        }
        if (localInputValue.length !== 0) {
            setLocalInputValue('')
            if (handleInput) {
                handleInput('')
            }
        }
    }
    const renderFilterOptions = React.useMemo(() => {
        if (name === 'productTypes') {
            return filterProductTypeOptions(productTypeOptions || [])
        }
        return options
    }, [productTypeOptions, options, name])
    const handleInputChange = (
        _event: React.ChangeEvent<{}>,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason !== 'reset') {
            setLocalInputValue(value)
            if (handleInput) {
                handleInput(value)
            }
        }
    }

    const handleResize = (width: number) => {
        setLocalWidth(width)
    }

    const handleFocus = () => {
        setHideInput(false)
        setLocalInputValue('')
    }

    const handleBlur = () => {
        setHideInput(true)
    }

    return (
        <>
            <Autocomplete
                data-testid={`listingFilter${name}`}
                disabled={disabled}
                className={clsx(
                    className,
                    `${name}Autocomplete`,
                    name !== 'product'
                        ? !fullWidth
                            ? classes.filterWidthNonFull
                            : classes.autocompleteContainer
                        : null
                )}
                classes={{
                    tag: fullWidth
                        ? classes.autocompleteTag
                        : classes.nonFullAutoCompleteTag,
                    popper: classes.popperStyle,
                }}
                multiple={type === 'multiple'}
                disableClearable={type === 'single'}
                fullWidth={fullWidth}
                openOnFocus
                size="small"
                limitTags={2}
                options={renderFilterOptions}
                value={value}
                inputValue={
                    type === 'multiple' ? localInputValue ?? '' : undefined
                }
                onChange={handleChange}
                onInputChange={handleInputChange}
                popupIcon={<ExpandMoreIcon fontSize="small" />}
                renderTags={
                    type === 'multiple'
                        ? (value, getTagProps) => {
                            return value.map((option, index) => (
                                <AutoCompleteTags
                                    key={index + localWidth}
                                    option={option}
                                    index={index}
                                    getTagProps={getTagProps}
                                    selectedDisplayItems={
                                        selectedDisplayItems
                                    }
                                    width={localWidth}
                                    fullWidth={fullWidth}
                                    isOpen={!hideInput}
                                />
                            ))
                        }
                        : undefined
                }
                selectOnFocus={false}
                renderInput={(params) => {
                    const { InputProps, ...restParams } = params
                    const { startAdornment, ...restInputProps } = InputProps

                    return (
                        <TextField
                            {...restParams}
                            error={isInputError}
                            InputLabelProps={{
                                ...params.InputLabelProps,
                                className: clsx(
                                    classes.labelText,
                                    classes.ellipsis
                                ),
                            }}
                            InputProps={{
                                ...restInputProps,
                                classes: {
                                    input: clsx(
                                        classes.text,
                                        type === 'single' && classes.pointer,
                                        { [classes.hide]: hideInput }
                                    ),
                                    root: clsx(
                                        label === '' || !label
                                            ? classes.autocompleteRootNoLabel
                                            : classes.autocompleteRoot,
                                        classes.autocompleteRemoveBorder
                                    ),
                                },
                                startAdornment:
                                    type === 'multiple' && !hideInput ? (
                                        value.length === 0 ? (
                                            startAdornment
                                        ) : (
                                            <div
                                                style={{
                                                    maxWidth:
                                                        'calc(100% - 1px)',
                                                    maxHeight: 74, // 2 tags height
                                                    overflowY: 'auto', // show vertical scrollbar when many items selected
                                                }}
                                            >
                                                {startAdornment}
                                            </div>
                                        )
                                    ) : (
                                        startAdornment
                                    ),
                                readOnly: type === 'single',
                                disabled: type === 'single',
                            }}
                            inputProps={{
                                ...params.inputProps,
                                'aria-autocomplete': 'none',
                                autoComplete: 'chrome-off',
                                className:
                                    type === 'multiple' && hideInput
                                        ? classes.autocompleteRootHidden
                                        : '',
                            }}
                            variant="outlined"
                            className={clsx(classes.textField, `${name}Input`)}
                            label={label}
                            type="text"
                        />
                    )
                }}
                renderOption={(option: string) => (
                    <Typography
                        component="span"
                        className={clsx(classes.option, classes.text)}
                    >
                        {getOptionText(option)}
                    </Typography>
                )}
                onOpen={handleOpen}
                onClose={handleClose}
                onFocus={type === 'multiple' ? handleFocus : undefined}
                onBlur={type === 'multiple' ? handleBlur : undefined}
                loading={loading}
                getOptionDisabled={(option) => {
                    if (type === 'multiple') {
                        const values = value as string[]
                        return values.some((each) => each === option)
                    }
                    return false
                }}
                disableCloseOnSelect={type === 'multiple'}
            />

            <ReactResizeDetector handleWidth onResize={handleResize} />
        </>
    )
}
